import { concatUrl } from '@hozana/router/concatUrl'
import { EVENT_ORIGIN, GTM_AUTH_SOURCE } from '@hozana/tracking/constants'

import type { TLocale } from 'i18n/constants'

import type { COMMUNITY_PAGE_TABS } from 'modules/community/constants'

import { API_PATHNAME as rawAPI_PATHNAME } from './api'
import { STATIC_PAGES_REVALIDATE, PAGE as rawPAGE } from './pages'

export { STATIC_PAGES_REVALIDATE }

export const API_PATHNAME = rawAPI_PATHNAME
export type API_PATHNAME = TObjectValues<typeof rawAPI_PATHNAME>

export const PAGE = rawPAGE
export type PAGE = TObjectValues<typeof rawPAGE>

export const IMMUTABLE_PREFIX = `/immutable/${CONF_KEYS.BUILD_ID}`

const addLocalePrefix = (locale: TLocale, path: string) => (locale === 'fr' ? path : `/${locale}${path}`)

export const getAdminRoutes = (locale: TLocale) => ({
  app: ({ utmMedium }: { utmMedium: string }) => addLocalePrefix(locale, concatUrl('/app', { utm_medium: utmMedium })),
  animatorSpace: () => addLocalePrefix(locale, '/admin/animator-space'),
  uploadAvatar: () => addLocalePrefix(locale, '/admin/upload-avatar'),
  communityManager: ({ communityId }: { communityId: number }) =>
    addLocalePrefix(locale, concatUrl('/admin/community/manager', { communityId: communityId.toString() })),
  communityEdit: ({ communityId }: { communityId: number }) =>
    addLocalePrefix(locale, concatUrl('/admin/community/edit', { communityId: communityId.toString() })),
  communityRoles: ({ communityId }: { communityId: number }) =>
    addLocalePrefix(locale, concatUrl('/admin/community/roles', { communityId: communityId.toString() })),
  communityStatistics: ({ communityId }: { communityId: number }) =>
    addLocalePrefix(locale, concatUrl('/admin/community/statistics', { communityId: communityId.toString() })),
  communityViewAdmin: ({ communityId, tab }: { communityId: number; tab?: COMMUNITY_PAGE_TABS }) =>
    addLocalePrefix(
      locale,
      concatUrl('/admin/community', { communityId: communityId.toString(), ...(tab && { tab }) }),
    ),
  publicationCreate: ({ communityId }: { communityId: number }) =>
    addLocalePrefix(locale, concatUrl('/admin/publication/create', { communityId: communityId.toString() })),
  publicationEdit: ({ publicationId }: { publicationId: number }) =>
    addLocalePrefix(locale, concatUrl('/admin/publication/edit', { publicationId: publicationId.toString() })),
  leave: () => addLocalePrefix(locale, '/leave'),
  testimonies: () =>
    addLocalePrefix(
      locale,
      {
        fr: '/temoignages',
        en: '/testimonies',
        es: '/testimonios',
        pt: '/testemunhos',
        it: '/testimonies',
        pl: '/testimonies',
      }[locale],
    ),
  page: (pageSlug: string) =>
    addLocalePrefix(
      locale,
      {
        fr: `/page/${pageSlug}`,
        en: `/page/${pageSlug}`,
        es: `/pagina/${pageSlug}`,
        pt: `/pagina/${pageSlug}`,
        it: `/pagina/${pageSlug}`,
        pl: `/strona/${pageSlug}`,
      }[locale],
    ),
  newPassword: () =>
    addLocalePrefix(
      locale,
      {
        fr: '/nouveau-mot-de-passe',
        en: '/new-password',
        es: '/nueva-contrasena',
        pt: '/nova-senha',
        it: '/new-password',
        pl: '/new-password',
      }[locale],
    ),
  contact: () =>
    addLocalePrefix(
      locale,
      {
        fr: '/nous-contacter',
        en: '/contact',
        es: '/contactar',
        pt: '/contatar',
        it: '/contattaci',
        pl: '/kontakt',
      }[locale],
    ),
})

export const newRoutes = {
  authComplete: (
    params: {
      redirectTo?: string
      authSource?: GTM_AUTH_SOURCE
      email?: string
      emailHash?: string
    } = {},
  ) => concatUrl('/auth/complete', params),
  communities: () => '/communities',
  communitiesCategory: (tagSlug: string) => `/communities/${tagSlug}`,
  community: (idSlug: string, origin: EVENT_ORIGIN | undefined) => concatUrl(`/community/${idSlug}`, { origin }),
  communityNotifications: ({ communityId }: { communityId: number }) => `/community/${communityId}/notifications`,
  contact: () => '/contact',
  conversations: () => '/messages',
  donateAuto: ({ locale, source }: { locale?: TLocale; source?: string }) =>
    concatUrl(`${CONF_KEYS.API_URL}/${locale}/donate/auto`, { utm_medium: `${source}_Hozana_v2` }),
  faq: () => newRoutes.page('faq'),
  intention: (intentionId: number, shareToken: string) => `/intention/${intentionId}-${shareToken}`,
  intentions: ({ source }: { source?: string }) => concatUrl('/intentions', { source }),
  newConversation: (userId: number) => concatUrl('/messages', { userId: userId.toString() }),
  page: (pageSlug: string) => `/page/${pageSlug}`,
  prayerSpace: () => '/prayer-space',
  preferences: ({ source }: { source?: string }) => concatUrl('/preferences', { source }),
  profile: () => '/profile',
  publication: (idSlug: string) => `/publications/${idSlug}`,
  settings: () => '/settings',
  settingsCommunities: () => '/settings/notifications/communities',
  guidesList: (locale: TLocale = 'fr') => addLocalePrefix(locale, '/guides-list'),
  guide: (guideName: string, locale: TLocale = 'fr') => addLocalePrefix(locale, `/${guideName}`),
}
